import React from 'react';
import { Clock, SignOut, ShieldCheck } from '@phosphor-icons/react';

/**
 * Modal that warns the user about an upcoming session timeout. The actual
 * scheduling/lifecycle lives in SessionTimeoutManager.
 */
export default function SessionTimeoutDialog({ secondsLeft, onStay, onLogout }) {
  // Soft floor so we never render "-1s" while React batches a state update.
  const display = Math.max(0, secondsLeft);

  return (
    <div
      data-testid="session-timeout-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
    >
      {/* Backdrop — non-dismissible (clicking outside should NOT silently
          close this; the user must make a conscious choice). */}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" aria-hidden="true" />

      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-[#e3dcd1] overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <Clock size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="session-timeout-title"
              data-testid="session-timeout-title"
              className="font-serif text-xl text-[#013523] tracking-tight"
            >
              Session expiring soon
            </h2>
            <p className="text-sm text-[#3a4a40] mt-1">
              Your session will expire soon. Save anything you&apos;re working on. You&apos;ll need to log back in when the timer ends.
            </p>
            <p
              data-testid="session-timeout-countdown-line"
              className="text-xs text-[#6a766e] mt-2 tabular-nums"
            >
              Time remaining:{' '}
              <span
                data-testid="session-timeout-countdown"
                className="font-semibold text-[#013523]"
              >
                {display}
              </span>{' '}
              {display === 1 ? 'second' : 'seconds'}
            </p>
          </div>
        </div>

        <div className="px-5 pb-5 pt-2 flex flex-col-reverse sm:flex-row gap-2">
          <button
            type="button"
            data-testid="session-timeout-logout-btn"
            onClick={onLogout}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[#e3dcd1] bg-white text-[#3a4a40] hover:bg-[#fbfaf8] text-sm font-medium"
          >
            <SignOut size={14} weight="bold" />
            Log out now
          </button>
          <button
            type="button"
            data-testid="session-timeout-stay-btn"
            onClick={onStay}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#015e3f] text-white hover:bg-[#013523] text-sm font-medium shadow-sm"
          >
            <ShieldCheck size={14} weight="bold" />
            Continue for now
          </button>
        </div>
      </div>
    </div>
  );
}
